"use client";

import type { FC } from "react";

import { AgentState } from "../../types/AgentState";
import {
  AgentAvatar,
  type AgentAvatarGradientColors,
  type AgentAvatarProps,
} from "./AgentAvatar";
import type { ThemeName } from "../../theme/types/ThemeName";
import type { ClassNameProp } from "../../props/ClassNameProp";

export type AgentAvatarControllerProps = ClassNameProp & {
  state?: AgentState | "intro" | "landing";
  size?: number;
  isSpeaking?: boolean;
  theme?: ThemeName;
};

/**
 * @see https://github.com/ezekielaquino/HuubAvatar
 * @see https://huub-avatar.vercel.app/
 */
export const AgentAvatarController: FC<AgentAvatarControllerProps> = ({
  size,
  isSpeaking = false,
  state = AgentState.IDLE,
  theme,
}) => {
  let animationType: AgentAvatarProps["animationType"] | undefined = "idle";

  switch (state) {
    case "landing":
      animationType = "bounceOut";
      break;
    case "intro":
      animationType = "spin";
      break;
    case AgentState.LOADING:
    case AgentState.TYPING:
      animationType = "moveHorizontal";
      break;
    case AgentState.LISTENING:
      animationType = "layerRotate";
      break;
    case AgentState.SPEAKING:
      animationType = "idle";
      break;
    case AgentState.ERROR:
      animationType = "drop";
      break;
  }

  const gradientColors: AgentAvatarGradientColors = (() => {
    const defaultColors: AgentAvatarGradientColors = [
      "#0E83FF",
      "#C467D5",
      "#FF6B6E",
    ];

    switch (theme) {
      case "dark":
        return [...defaultColors].reverse() as AgentAvatarGradientColors;
      case "unive":
        return ["#E0E0FF", "#AEAEFF", "#9D9DFF"];
      case "deHypotheker":
        return ["#D8EDF4", "#8DC9E2", "#6EB5E2"];
      case "ing":
        return ["#FFD4B8", "#FF8C48", "#FF6200"];
      case "rabobank":
        return ["#97B6EA", "#4F86E1", "#3776DE"];
      case "quion":
        return ["#CBE5F4", "#83BEE2", "#6BB1DC"];
      case "nationaleNederlanden":
        return ["#FDDCB5", "#F5A34E", "#F0911F"];
      case "vanLanschotKempen":
        return ["#B7E4E4", "#6CC3C3", "#53B8B8"];
      case "verenigingEigenHuis":
        return ["#C5B8E3", "#4D3895", "#352171"];
      case "nibc":
        return ["#B3E6F3", "#40BBDE", "#00A5D6"];
      case "munt":
        return ["#B7F6E4", "#0CDEA7", "#00D796"];
      case "hypotheekvisie":
        return ["#B3E6F3", "#40BBDE", "#00A5D6"];
      case "obvion":
        return ["#FAE8F0", "#D088B0", "#A90061"];
      case "vista":
        return ["#C8D0E0", "#455587", "#263460"];
      case "asr":
        return ["#D4F0E5", "#8DD4B8", "#4AB286"];
      case "anwb":
        return ["#C4DBEF", "#4879AE", "#16498B"];
      case "eviVanLanschot":
        return ["#DFD2EC", "#9266AD", "#6c3987"];
      case "allianz":
        return ["#B3C6E0", "#4070A3", "#003781"];
      case "fnv":
        return ["#B3E5F7", "#33B4E7", "#009CDE"];
      case "randstad":
        return ["#B3D9F5", "#3397E1", "#007BD7"];
      case "achmea":
        return ["#C4BFD9", "#706498", "#504785"];
      case "funda":
        return ["#FDD9A0", "#F6A530", "#F39200"];
      case "nvm":
        return ["#F2B8C2", "#E05070", "#D63B5C"];
      case "abnamro":
        return ["#CCE0E0", "#00A296", "#004C4C"];
      case "domek":
        return ["#C8E8F6", "#38A9DD", "#189DD9"];
      default:
        return defaultColors;
    }
  })();

  return (
    <AgentAvatar
      animationType={animationType}
      isListening={isSpeaking && state === AgentState.SPEAKING}
      size={size}
      gradientColors={gradientColors}
    />
  );
};
